import DocumentsTable from 'components/common/DocumentsTable';
import { CloudStorageObject } from 'interfaces/CloudStorageObject';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router';
import UserContext from '../../Context/UserProvider';
import { Box, Container, Grid, TextField, Button, CircularProgress, Typography } from '@mui/material';
import DashboardCard from 'components/dashboard/common/DashboardCard';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import { useApiService } from '../../services/ApiService/ApiService';

type SignedUrlResponse = {
    ramsDocuments: CloudStorageObject[];
};

function AgreementDocumentsEditPage(props: any) {

    const { userInfo } = useContext(UserContext)
    const { fetchWithAuth } = useApiService();
    const [ramsDocuments, setRamsDocuments] = useState<CloudStorageObject[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const location = useLocation() as any;
    const [chargingPartnerAgreementDocumentSetUuid, setChargingPartnerAgreementDocumentSetUuid] = useState(location.state?.chargingPartnerAgreementDocumentSetUuid);

    useEffect(() => {

        if (location.state?.chargingPartnerAgreementDocumentSetUuid) {
            setChargingPartnerAgreementDocumentSetUuid(location.state.chargingPartnerAgreementDocumentSetUuid);
        }
    }, [location.state?.chargingPartnerAgreementDocumentSetUuid]);

    const initialState = {
        surveyName: "",
    }

    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [documentFiles, setDocumentFiles] = useState<CloudStorageObject[]>([]);
    const [documentSetName, setDocumentSetName] = useState("");


    useEffect(() => {
        // Initialize document and video files separately from ramsDocuments
        setDocumentFiles(ramsDocuments);
    }, [ramsDocuments]);

    // Use the FileList type for the files state.
    const handleDocumentChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const newFiles: CloudStorageObject[] = Array.from(event.target.files).map((file, index) => ({
                id: `temp-${Date.now()}-${index}`, // Temporary unique ID
                name: file.name,
                status: 'Not Saved' as 'Not Saved', // Custom status to indicate the file is not yet saved
                file: file, // Store the file object for uploading
                putSignedUrl: "",
                deleteSignedUrl: "",
                fullName: ""
            }));

            // Add new files to the start of the array
            setRamsDocuments(prev => [...newFiles, ...prev]);
        }
    };



    const removeDocument = (id: string) => {
        setDocumentFiles(prev => prev.filter(doc => doc.id !== id));
    };

    const save = async () => {
        setIsUploading(true); // Indicate the start of the upload process

        // Merge documentFiles and videoFiles to create a new snapshot of ramsDocuments
        const updatedRamsDocuments = [...documentFiles];

        try {
            // Handle deletion of documents marked as 'Deleted'
            const toDelete = updatedRamsDocuments.filter(doc => doc.status === 'Deleted');

            const deletePromises = toDelete.map(doc =>
                fetch(doc.deleteSignedUrl, {
                    method: 'DELETE',
                })
            );
            await Promise.all(deletePromises);

            // Remove deleted documents from the updatedRamsDocuments before upload operations
            let postDeleteDocuments = updatedRamsDocuments.filter(doc => doc.status !== 'Deleted');

            // Call submitSurvey here to ensure it's always executed
            const chargingPartnerAgreementDocumentSetUuid = await submitDocumentSet();

            // Filter out 'Not Saved' documents that have a file to upload
            const toUpload = postDeleteDocuments.filter(doc => doc.status === 'Not Saved' && doc.file);

            if (toUpload.length > 0) {
                const fileInfos = toUpload.map(doc => ({ name: doc.name}));

                const urls = await fetchSignedUrls(fileInfos, chargingPartnerAgreementDocumentSetUuid);

                const uploadPromises = urls.map((url, index) => {
                    const file = toUpload[index].file!; // Non-null assertion is safe due to the filter
                    return fetch(url.putSignedUrl, {
                        method: 'PUT',
                        body: file,
                        headers: {
                            'Content-Type': file.type,
                        },
                    });
                });

                await Promise.all(uploadPromises);

                // Update the status of uploaded documents to 'Saved'
                postDeleteDocuments = postDeleteDocuments.map(doc => ({
                    ...doc,
                    status: doc.status === 'Not Saved' && doc.file ? 'Saved' : doc.status
                }));
            }

            // Set the final state of ramsDocuments
            setRamsDocuments(postDeleteDocuments);

            toast.success('Agreement Document Set saved.', {
                position: 'bottom-center',
                duration: 5000,
            });
        } catch (error) {
            setErrorMessage("Error saving Agreement Document Set");
            console.error('Error uploading files:', error);
        }

        setIsUploading(false); // Indicate the end of the upload process
    };

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage, {
                position: 'bottom-center',
                duration: 5000,
            });
        }
        setErrorMessage(null);
    }, [errorMessage]);


    // Update the type for files parameter to match the expected structure
    const fetchSignedUrls = async (fileInfos: { name: string; }[], chargingPartnerAgreementDocumentSetUuid: string): Promise<CloudStorageObject[]> => {
        try {
            const response = await fetchWithAuth('/rams/signedurls/get', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ 
                    files: fileInfos, 
                    businessUuid: userInfo.businessUuid, 
                    chargingPartnerAgreementDocumentSetUuid: chargingPartnerAgreementDocumentSetUuid 
                }),
            });

            if (!response.ok) {
                setIsError(true);
                throw new Error('Failed to fetch signed URLs');
            }

            const data: SignedUrlResponse = await response.json();
            return data.ramsDocuments;
        } catch (error) {
            console.error('Error fetching signed URLs:', error);
            setIsError(true);
            throw error;
        }
    };

    const updateDocuments = (updatedDocs: CloudStorageObject[]) => {
            setDocumentFiles(updatedDocs);
    };


    const fetchSurvey = async () => {
        try {
            if (chargingPartnerAgreementDocumentSetUuid != null) {
                const response = await fetchWithAuth('/charging-partner/agreement-document-set/get', {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        uuid: userInfo.uuid,
                        businessUuid: userInfo.businessUuid,
                        chargingPartnerAgreementDocumentSetUuid: chargingPartnerAgreementDocumentSetUuid
                    }),
                });

                if (!response.ok) {
                    setIsError(true);
                    return;
                }

                const res_json = await response.json();
                setRamsDocuments(res_json.ramsDocuments.map((e: any) => ({ 
                    ...e, 
                    id: e.fullName, 
                    name: e.name, 
                    type: e.type, 
                    fullName: e.fullName, 
                    signedUrl: e.signedUrl 
                })));
                setDocumentSetName(res_json.documentSet.name);
            }
        } catch (error) {
            console.error('Error fetching survey:', error);
            setIsError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSurvey();
    }, []);

    function validateForm() {

        if (documentSetName === '') {
            return false
        }

        return true

    }

    const submitDocumentSet = async () => {
        const validationStatus = validateForm()
        if (!validationStatus) {
            return null; // Return null if validation fails
        }

        try {
            const response = await fetchWithAuth('/charging-partner/agreement-document-set/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    businessUuid: userInfo.businessUuid,
                    uuid: userInfo.uuid,
                    documentSetName: documentSetName,
                    chargingPartnerAgreementDocumentSetUuid: chargingPartnerAgreementDocumentSetUuid,
                    ramsDocuments: ramsDocuments,
                })
            });

            if (!response.ok) {
                setIsError(true);
                return null; // Return null in case of HTTP errors
            }

            const res_json = await response.json();
            return res_json.uuid; // Assuming 'uuid' is the name of the key where the UUID is stored
        } catch (error) {
            console.error('Error submitting document set:', error);
            setIsError(true);
            return null;
        }
    };



    function onChange(e: any) {


        let action: any
        if (e.target) {
            action = {
                input: e.target.name,
                value: e.target.value,
            }

        } else {
            action = {
                input: e.name,
                value: e.value,
            }
        }

        if (action.input === "documentSetName") {
            setDocumentSetName(action.value);
        }

    }

    return (
        <>
            {isError ? (
                <ErrorPage statusCode='505' />
            ) : (
                <>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Container maxWidth="xl">
                            <Box sx={{ pr: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <DashboardCard
                                            title="Agreement Document Set Details"
                                            icon={<DescriptionIcon />}
                                            accentColor="primary"
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Agreement Document Set Name"
                                                    name="documentSetName"
                                                    value={documentSetName}
                                                    onChange={onChange}
                                                    required
                                                />
                                            </Box>
                                        </DashboardCard>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <DashboardCard
                                            title="Documents"
                                            icon={<UploadFileIcon />}
                                            accentColor="primary"
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                <Box>
                                                    <Button
                                                        variant="contained"
                                                        component="label"
                                                        startIcon={<UploadFileIcon />}
                                                    >
                                                        Upload Files
                                                        <input
                                                            type="file"
                                                            multiple
                                                            onChange={handleDocumentChange}
                                                            style={{ display: 'none' }}
                                                        />
                                                    </Button>
                                                </Box>

                                                <Box>
                                                    <DocumentsTable
                                                        data={documentFiles}
                                                        onRemove={(id) => removeDocument(id)}
                                                        onUpdate={(docs) => updateDocuments(docs)}
                                                    />
                                                </Box>
                                            </Box>
                                        </DashboardCard>

                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => save()}
                                                startIcon={<SaveIcon />}
                                                disabled={isUploading}
                                            >
                                                Save
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Container>
                    )}

                    {isUploading && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 9999,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </>
            )}
        </>
    );
}

export default AgreementDocumentsEditPage;
